import React, { useState } from 'react';
import '../AuthWarningModal.css';

// Predefined color palette for categories
const CATEGORY_COLORS = [
  { bg: '#dbeafe', text: '#1d4ed8' }, // Blue (NSK)
  { bg: '#f3e8ff', text: '#7c3aed' }, // Purple (OTHER)
  { bg: '#dcfce7', text: '#166534' }, // Green
  { bg: '#fef3c7', text: '#92400e' }, // Yellow
  { bg: '#fce7f3', text: '#be185d' }, // Pink
  { bg: '#e0e7ff', text: '#4338ca' }, // Indigo
  { bg: '#fef2f2', text: '#991b1b' }, // Red
  { bg: '#ecfdf5', text: '#065f46' }, // Emerald
  { bg: '#fff7ed', text: '#9a3412' }, // Orange
  { bg: '#f0f9ff', text: '#0c4a6e' }, // Sky
  { bg: '#f5f3ff', text: '#5b21b6' }, // Violet
  { bg: '#fefce8', text: '#713f12' }, // Lime
];

// Generate a stable color for a category based on its name
const getCategoryColor = (categoryName) => {
  // Use predefined colors for known categories
  const lowerName = categoryName.toLowerCase();
  if (lowerName === 'nsk') {
    return CATEGORY_COLORS[0];
  }
  if (lowerName === 'other') {
    return CATEGORY_COLORS[1];
  }
  
  // For other categories, use a hash function to get a stable color
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[colorIndex];
};

function ReceiptScopeTab({ config, onUpdate, onAddTenant, onDeleteTenant }) {
  const merchants = config?.eligibleTenants || [];
  const categories = config?.tenantCategories || ['NSK', 'OTHER'];
  const defaultCategory = categories[0] || 'OTHER';

  const [newMerchant, setNewMerchant] = useState({ name: '', category: defaultCategory });
  const [isAdding, setIsAdding] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingMerchantId, setEditingMerchantId] = useState(null);
  const [editingMerchantData, setEditingMerchantData] = useState({ name: '', category: '' });
  const [saving, setSaving] = useState(false);
  
  // Modal states
  const [showDeleteMerchantModal, setShowDeleteMerchantModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({ title: '', message: '' });
  const [pendingDeleteMerchantId, setPendingDeleteMerchantId] = useState(null);
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null);

  const handleAddMerchant = async () => {
    if (!newMerchant.name.trim()) return;
    const categoryToSend = categories.includes(newMerchant.category) ? newMerchant.category : defaultCategory;
    await onAddTenant({ name: newMerchant.name.trim(), category: categoryToSend });
    setNewMerchant({ name: '', category: defaultCategory });
    setIsAdding(false);
  };

  const openAddMerchant = () => {
    setNewMerchant({ name: '', category: defaultCategory });
    setIsAdding(true);
  };

  const handleDeleteMerchant = (id) => {
    setPendingDeleteMerchantId(id);
    setShowDeleteMerchantModal(true);
  };

  const confirmDeleteMerchant = async () => {
    if (pendingDeleteMerchantId !== null) {
      await onDeleteTenant(pendingDeleteMerchantId);
      setShowDeleteMerchantModal(false);
      setPendingDeleteMerchantId(null);
    }
  };

  // Start editing a merchant
  const startEditMerchant = (merchant) => {
    setEditingMerchantId(merchant.id);
    setEditingMerchantData({ name: merchant.name, category: merchant.category });
  };

  // Save merchant edit
  const saveEditMerchant = async () => {
    if (!editingMerchantData.name.trim()) return;
    
    setSaving(true);
    try {
      // Update the merchant in the eligibleTenants array
      const updatedMerchants = merchants.map(t => 
        t.id === editingMerchantId 
          ? { ...t, name: editingMerchantData.name.trim(), category: editingMerchantData.category }
          : t
      );
      await onUpdate({ eligibleTenants: updatedMerchants });
      setEditingMerchantId(null);
      setEditingMerchantData({ name: '', category: '' });
    } catch (error) {
      console.error('Failed to update merchant:', error);
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const cancelEditMerchant = () => {
    setEditingMerchantId(null);
    setEditingMerchantData({ name: '', category: '' });
  };

  // Add new category
  const handleAddCategory = async () => {
    const categoryName = newCategory.trim().toUpperCase();
    if (!categoryName) return;
    if (categories.includes(categoryName)) {
      setMessageModalContent({ title: 'Error', message: 'This category already exists' });
      setShowMessageModal(true);
      return;
    }
    
    setSaving(true);
    try {
      // Update tenantCategories and add default values to amountThresholds and categoryLimits
      const updatedAmountThresholds = {
        ...(config?.amountThresholds || {}),
        [categoryName]: 0 // Default threshold for new category
      };
      const updatedCategoryLimits = {
        ...(config?.receiptQuantityRules?.categoryLimits || {}),
        [categoryName]: 1 // Default limit for new category
      };
      
      await onUpdate({ 
        tenantCategories: [...categories, categoryName],
        amountThresholds: updatedAmountThresholds,
        receiptQuantityRules: {
          ...(config?.receiptQuantityRules || {}),
          categoryLimits: updatedCategoryLimits
        }
      });
      setNewCategory('');
      setIsAddingCategory(false);
    } catch (error) {
      console.error('Failed to add category:', error);
    } finally {
      setSaving(false);
    }
  };

  // Delete category
  const handleDeleteCategory = (categoryToDelete) => {
    // Check if any merchant is using this category
    const merchantsUsingCategory = merchants.filter(t => t.category === categoryToDelete);
    if (merchantsUsingCategory.length > 0) {
      setMessageModalContent({ 
        title: 'Error', 
        message: `Cannot delete "${categoryToDelete}" - ${merchantsUsingCategory.length} merchant(s) are using this category. Please reassign them first.` 
      });
      setShowMessageModal(true);
      return;
    }
    
    setPendingDeleteCategory(categoryToDelete);
    setShowDeleteCategoryModal(true);
  };

  const confirmDeleteCategory = async () => {
    if (!pendingDeleteCategory) return;
    
    const categoryToDelete = pendingDeleteCategory;
    setShowDeleteCategoryModal(false);
    setSaving(true);
    try {
      const updatedCategories = categories.filter(c => c !== categoryToDelete);
      
      // Also remove from amountThresholds and categoryLimits
      const updatedAmountThresholds = { ...(config?.amountThresholds || {}) };
      delete updatedAmountThresholds[categoryToDelete];
      
      const updatedCategoryLimits = { ...(config?.receiptQuantityRules?.categoryLimits || {}) };
      delete updatedCategoryLimits[categoryToDelete];
      
      await onUpdate({ 
        tenantCategories: updatedCategories,
        amountThresholds: updatedAmountThresholds,
        receiptQuantityRules: {
          ...(config?.receiptQuantityRules || {}),
          categoryLimits: updatedCategoryLimits
        }
      });
    } catch (error) {
      console.error('Failed to delete category:', error);
      setMessageModalContent({ title: 'Error', message: 'Failed to delete category: ' + (error.message || 'Unknown error') });
      setShowMessageModal(true);
    } finally {
      setSaving(false);
      setPendingDeleteCategory(null);
    }
  };

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>Receipt Scope Rules</h2>
        <p>Define which merchants/stores are eligible for claims. Only receipts from these merchants will be accepted.</p>
      </div>

      <div className="rules-form">
        <div className="form-section">
          <div className="form-section-title">Eligible Merchants</div>
          
          <div className="items-list">
            {merchants.length === 0 ? (
              <div className="empty-state-mini">
                <p>No merchants configured. Add merchants to define eligible stores.</p>
              </div>
            ) : (
              merchants.map(merchant => (
                <div key={merchant.id} className="item-card">
                  {editingMerchantId === merchant.id ? (
                    // Edit mode
                    <>
                      <div className="item-info" style={{ flex: 1, gap: '0.75rem' }}>
                        <input
                          type="text"
                          value={editingMerchantData.name}
                          onChange={(e) => setEditingMerchantData({ ...editingMerchantData, name: e.target.value })}
                          style={{ 
                            flex: 1,
                            padding: '0.5rem 0.75rem',
                            border: '1.5px solid #3b82f6',
                            borderRadius: '6px',
                            fontSize: '0.9375rem'
                          }}
                          autoFocus
                        />
                        <select
                          value={editingMerchantData.category}
                          onChange={(e) => setEditingMerchantData({ ...editingMerchantData, category: e.target.value })}
                          style={{
                            padding: '0.5rem 0.75rem',
                            border: '1.5px solid #3b82f6',
                            borderRadius: '6px',
                            fontSize: '0.9375rem',
                            minWidth: '120px'
                          }}
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <div className="item-actions">
                        <button 
                          className="btn-icon-sm"
                          onClick={saveEditMerchant}
                          disabled={saving}
                          title="Save"
                          style={{ background: '#dcfce7', color: '#166534' }}
                        >
                          ✓
                        </button>
                        <button 
                          className="btn-icon-sm"
                          onClick={cancelEditMerchant}
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    </>
                  ) : (
                    // View mode
                    <>
                      <div className="item-info">
                        <div className="item-details">
                          <h4>{merchant.name}</h4>
                          <span 
                            className="category-badge"
                            style={{
                              background: getCategoryColor(merchant.category).bg,
                              color: getCategoryColor(merchant.category).text
                            }}
                          >
                            {merchant.category}
                          </span>
                        </div>
                      </div>
                      <div className="item-actions">
                        <button 
                          className="btn-icon-sm"
                          onClick={() => startEditMerchant(merchant)}
                          title="Edit merchant"
                          style={{ background: '#e0f2fe', color: '#0369a1' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button 
                          className="btn-icon-sm delete"
                          onClick={() => handleDeleteMerchant(merchant.id)}
                          title="Remove merchant"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}

            {isAdding ? (
              <div className="item-card" style={{ background: '#f8fafc' }}>
                <div className="item-info" style={{ flex: 1, gap: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder="Merchant name (e.g., NSK Trade City)"
                    value={newMerchant.name}
                    onChange={(e) => setNewMerchant({ ...newMerchant, name: e.target.value })}
                    style={{ 
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '0.9375rem'
                    }}
                    autoFocus
                  />
                  <select
                    value={categories.includes(newMerchant.category) ? newMerchant.category : defaultCategory}
                    onChange={(e) => setNewMerchant({ ...newMerchant, category: e.target.value })}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '0.9375rem',
                      minWidth: '120px'
                    }}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="item-actions">
                  <button 
                    className="btn-icon-sm"
                    onClick={handleAddMerchant}
                    title="Save"
                    style={{ background: '#dcfce7', color: '#166534' }}
                  >
                    ✓
                  </button>
                  <button 
                    className="btn-icon-sm"
                    onClick={() => {
                      setIsAdding(false);
                      setNewMerchant({ name: '', category: defaultCategory });
                    }}
                    title="Cancel"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <button className="add-item-btn" onClick={openAddMerchant}>
                <span>+</span> Add Merchant
              </button>
            )}
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-title">Merchant Categories</div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
            Categories are used to group merchants and apply different rules per category.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {categories.map(cat => {
              const color = getCategoryColor(cat);
              return (
                <div 
                  key={cat} 
                  className="category-badge"
                  style={{ 
                    fontSize: '0.875rem', 
                    padding: '0.375rem 0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: color.bg,
                    color: color.text
                  }}
                >
                  {cat}
                  <button
                    onClick={() => handleDeleteCategory(cat)}
                    disabled={saving}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0',
                      fontSize: '0.75rem',
                      opacity: 0.7,
                      marginLeft: '0.25rem',
                      color: color.text
                    }}
                    title={`Delete ${cat} category`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            
            {isAddingCategory ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCategory();
                    if (e.key === 'Escape') {
                      setIsAddingCategory(false);
                      setNewCategory('');
                    }
                  }}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1.5px solid #3b82f6',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    width: '120px',
                    textTransform: 'uppercase'
                  }}
                  autoFocus
                />
                <button
                  onClick={handleAddCategory}
                  disabled={saving || !newCategory.trim()}
                  style={{
                    background: '#dcfce7',
                    color: '#166534',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.375rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setIsAddingCategory(false);
                    setNewCategory('');
                  }}
                  style={{
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.375rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAddingCategory(true)}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '20px',
                  padding: '0.375rem 0.875rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                + Add Category
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete Merchant Confirmation Modal */}
      {showDeleteMerchantModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowDeleteMerchantModal(false); setPendingDeleteMerchantId(null); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Remove Merchant</h2>
            <p className="auth-modal-message">Are you sure you want to remove this merchant?</p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowDeleteMerchantModal(false); setPendingDeleteMerchantId(null); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmDeleteMerchant}
                style={{ 
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Confirmation Modal */}
      {showDeleteCategoryModal && pendingDeleteCategory && (
        <div className="auth-modal-overlay" onClick={() => { setShowDeleteCategoryModal(false); setPendingDeleteCategory(null); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Delete Category</h2>
            <p className="auth-modal-message">Are you sure you want to delete the category "{pendingDeleteCategory}"?</p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowDeleteCategoryModal(false); setPendingDeleteCategory(null); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmDeleteCategory}
                style={{ 
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal (Error/Success) */}
      {showMessageModal && (
        <div className="auth-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">{messageModalContent.title}</h2>
            <p className="auth-modal-message">{messageModalContent.message}</p>
            <button 
              className="auth-modal-button"
              onClick={() => setShowMessageModal(false)}
              style={{ 
                background: messageModalContent.title === 'Error' 
                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceiptScopeTab;
